import { Component } from '@theme/component';
import { onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * How long to wait after the pointer leaves the menu before closing.
 * Gives the user time to slip diagonally from the pill into the panel
 * without losing the open state.
 */
const CLOSE_DELAY_MS = 150;

/**
 * Length of the close transition. Must match `--menu-panel-duration`
 * in blocks/_header-menu.liquid so we don't reset the row state until
 * the visual close animation has finished.
 */
const CLOSE_TRANSITION_MS = 220;

/**
 * Header menu state machine.
 *
 * The row's `data-menu-state` (closed | open | closing) plus
 * `data-active-index` form the single source of truth for the open
 * dropdown. CSS reads them directly:
 *
 *   - .menu-dropdown[data-active] is the active panel
 *   - .header__row[data-menu-state="open"] keeps the pill bg active
 *   - .header__row[data-menu-state="closing"] keeps the panel paint
 *     during the close transition
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The <overflow-list> element.
 * @property {HTMLElement[]} [dropdown] - Each top-level dropdown panel.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeTimer = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #closingResetTimer = null;
  /** @type {string | null} The currently active index ("1", "2", ... or "more"). */
  #activeIndex = null;
  /**
   * When the More panel is active, which overflow item's right-pane is
   * shown. Tracked separately from #activeIndex because all overflow
   * items share the same shadow-DOM panel.
   * @type {string | null}
   */
  #activeOverflowIndex = null;
  /**
   * Tracks which panels have a pointerleave listener attached so we
   * never double-attach. Panels appear lazily (the overflow shadow
   * panel only after <overflow-list> initializes), so this is filled
   * on demand from #open().
   * @type {WeakSet<HTMLElement>}
   */
  #panelsWithListeners = new WeakSet();

  connectedCallback() {
    super.connectedCallback();

    const signal = this.#abortController.signal;

    this.addEventListener('pointerover', this.#onPointerOver, { signal });
    this.addEventListener('pointerleave', this.#onPointerLeaveRoot, { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    document.addEventListener('pointerdown', this.#onDocPointerDown, { signal });

    // Pre-attach pointerleave to any light-DOM panels that already
    // exist. The shadow-DOM overflow panel is attached lazily on
    // first open (#open).
    this.querySelectorAll('.menu-dropdown').forEach((panel) => {
      if (panel instanceof HTMLElement) this.#attachPanelLeaveListener(panel);
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#clearCloseTimer();
    this.#clearClosingResetTimer();
  }

  /**
   * The .header__row that holds the state attributes.
   * @returns {HTMLElement | null}
   */
  get #headerRow() {
    return /** @type {HTMLElement | null} */ (this.closest('.header__row'));
  }

  /**
   * Lookup the dropdown panel matching an index.
   * Top-level items use numeric strings ("1", "2", ...) and the "More"
   * overflow uses "more".
   * @param {string} index
   * @returns {HTMLElement | null}
   */
  #getPanel(index) {
    if (index === 'more') {
      // Shadow DOM panel from <overflow-list>.
      const overflow = /** @type {HTMLElement | null} */ (
        this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
      );
      return overflow ?? null;
    }
    return /** @type {HTMLElement | null} */ (this.querySelector(`.menu-dropdown[data-menu-index="${CSS.escape(index)}"]`));
  }

  /**
   * Resolve the menu index for a given event target. Returns:
   *   - "1", "2", ... for a default-slot top-level item
   *   - "more"        for the More button (or any overflow item)
   *   - null          if the event isn't inside an interactive item
   * @param {EventTarget | null} target
   * @returns {string | null}
   */
  #resolveIndex(target) {
    if (!(target instanceof Element)) return null;

    // Hovering the dropdown panel itself keeps it open via its
    // [data-menu-index]. Note that overflow shadow-DOM hovering is
    // handled in #onPointerOver via composedPath().
    const panel = target.closest('.menu-dropdown[data-menu-index]');
    if (panel instanceof HTMLElement) {
      const idx = panel.dataset.menuIndex;
      // Items currently in the overflow slot map to "more".
      const li = /** @type {HTMLElement | null} */ (this.querySelector(`.menu-list__list-item[data-menu-index="${CSS.escape(idx ?? '')}"]`));
      if (li?.getAttribute('slot') === 'overflow') return 'more';
      return idx ?? null;
    }

    const li = target.closest('.menu-list__list-item');
    if (!(li instanceof HTMLElement)) return null;

    const idx = li.dataset.menuIndex;
    if (!idx) return null;

    // Items currently slotted into the overflow group share the
    // single overflow panel, plus the "More" button itself.
    if (li.getAttribute('slot') === 'overflow' || li.getAttribute('slot') === 'more') {
      return 'more';
    }

    return idx;
  }

  /**
   * Whether the resolved index has a real dropdown panel to open.
   * Top-level items without children render no panel; hovering them
   * should close any active panel rather than re-open one. The
   * "more" panel only counts when there's at least one overflowed
   * item — otherwise the shadow [part="overflow"] panel exists but
   * is empty.
   * @param {string | null} index
   */
  #hasPanel(index) {
    if (index == null) return false;
    if (index === 'more') {
      const hasOverflow = !!this.querySelector('.menu-list__list-item[slot="overflow"]');
      if (!hasOverflow) return false;
    }
    return this.#getPanel(index) !== null;
  }

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    // pointerover bubbles, fires for every element transition. We only
    // care about transitions that change the resolved index.
    const path = event.composedPath?.() ?? [event.target];
    let index = null;
    /** @type {Element | null} */
    let hoveredLi = null;
    for (const node of path) {
      if (!(node instanceof Element)) continue;
      const li = node.closest('.menu-list__list-item');
      if (li instanceof HTMLElement && !hoveredLi) hoveredLi = li;
      const resolved = this.#resolveIndex(node);
      if (resolved != null) {
        index = resolved;
        break;
      }
    }

    if (index == null) return;

    if (this.#hasPanel(index)) {
      this.#open(index);
      // If we're inside the More panel, pick which overflow row's
      // right-pane is shown based on the actual hovered <li>.
      if (index === 'more' && hoveredLi instanceof HTMLElement) {
        const slot = hoveredLi.getAttribute('slot');
        if (slot === 'overflow') {
          const ovIdx = hoveredLi.dataset.menuIndex;
          if (ovIdx) this.#setActiveOverflow(ovIdx);
        }
      }
    } else {
      // Hovering a link without a panel = close any active panel.
      this.#scheduleClose();
    }
  };

  /** @param {PointerEvent} event */
  #onPointerLeaveRoot = (event) => {
    // Leaving the menu bar's bounding box. The active panel hangs
    // visually below the bar; the panel's own pointerleave handler
    // closes if the cursor exits IT to the page. Don't schedule a
    // close if the cursor entered any descendant — including the
    // light-DOM .menu-dropdown panels and the <overflow-list> host
    // (relatedTarget for the shadow [part="overflow"] panel is
    // retargeted to the <overflow-list> host).
    if (!this.#activeIndex) return;
    const related = /** @type {Node | null} */ (event.relatedTarget);
    if (related && this.contains(related)) return;
    this.#scheduleClose();
  };

  /** @param {PointerEvent} event */
  #onPanelPointerLeave = (event) => {
    if (!this.#activeIndex) return;
    const related = /** @type {Node | null} */ (event.relatedTarget);
    // Stay open if cursor moved back into the menu bar OR another
    // descendant of header-menu (e.g. a sibling panel during switch).
    if (related && this.contains(related)) return;
    this.#scheduleClose();
  };

  /** @param {HTMLElement} panel */
  #attachPanelLeaveListener(panel) {
    if (this.#panelsWithListeners.has(panel)) return;
    this.#panelsWithListeners.add(panel);
    panel.addEventListener('pointerleave', this.#onPanelPointerLeave, {
      signal: this.#abortController.signal,
    });
    // Re-entering the panel cancels any pending close. (pointerover
    // on the menu bar already handles bar re-entry.)
    panel.addEventListener('pointerenter', this.#onPanelPointerEnter, {
      signal: this.#abortController.signal,
    });
  }

  #onPanelPointerEnter = () => {
    this.#clearCloseTimer();
    // If we were mid-close transition, reopen by rolling state back.
    if (this.#activeIndex && this.#headerRow?.dataset.menuState === 'closing') {
      this.#clearClosingResetTimer();
      this.#headerRow.dataset.menuState = 'open';
    }
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    const index = this.#resolveIndex(event.target);
    if (index == null) return;
    if (this.#hasPanel(index)) this.#open(index);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!this.#activeIndex) return;
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (!next) {
      this.#scheduleClose();
      return;
    }
    // Stay open if focus moves into the menu or its active panel.
    if (this.contains(next)) return;
    const panel = this.#getPanel(this.#activeIndex);
    if (panel?.contains(next)) return;
    this.#scheduleClose();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#activeIndex) return;
    this.#close({ immediate: true });
  };

  /** @param {PointerEvent} event */
  #onDocPointerDown = (event) => {
    if (!this.#activeIndex) return;
    const target = /** @type {Node | null} */ (event.target);
    if (target && this.contains(target)) return;
    const panel = this.#getPanel(this.#activeIndex);
    if (panel && target && panel.contains(target)) return;
    this.#close({ immediate: true });
  };

  /**
   * Open (or switch to) the panel for `index`.
   * @param {string} index
   */
  #open(index) {
    this.#clearCloseTimer();
    this.#clearClosingResetTimer();

    if (this.#activeIndex === index && this.#headerRow?.dataset.menuState === 'open') {
      return;
    }

    const row = this.#headerRow;
    if (!row) return;

    if (this.#activeIndex !== index) {
      this.dispatchEvent(new MegaMenuHoverEvent());

      // Drop the active flag from the previous panel before flipping
      // to the new one so the previous one fades out cleanly.
      if (this.#activeIndex) {
        const prevPanel = this.#getPanel(this.#activeIndex);
        prevPanel?.removeAttribute('data-active');
        this.#setLinkExpanded(this.#activeIndex, false);
        if (this.#activeIndex === 'more') this.#clearActiveOverflow();
      }

      this.#activeIndex = index;
      const panel = this.#getPanel(index);
      panel?.setAttribute('data-active', '');
      if (panel) this.#attachPanelLeaveListener(panel);
      this.#setLinkExpanded(index, true);

      if (index === 'more') {
        // Default the right-pane to the first overflow item.
        const firstOverflow = /** @type {HTMLElement | null} */ (
          this.querySelector('.menu-list__list-item[slot="overflow"]')
        );
        const idx = firstOverflow?.dataset.menuIndex;
        if (idx) this.#setActiveOverflow(idx);
      }
    }

    row.dataset.menuState = 'open';
    row.dataset.activeIndex = index;
    this.ariaExpanded = 'true';
  }

  /**
   * Highlight a specific overflow item's right-pane content. Only
   * meaningful while #activeIndex === "more".
   * @param {string} index
   */
  #setActiveOverflow(index) {
    if (this.#activeOverflowIndex === index) return;
    if (this.#activeOverflowIndex) {
      const prev = this.querySelector(
        `.menu-list__list-item[data-menu-index="${CSS.escape(this.#activeOverflowIndex)}"] [aria-haspopup]`
      );
      prev?.setAttribute('aria-expanded', 'false');
    }
    this.#activeOverflowIndex = index;
    const next = this.querySelector(
      `.menu-list__list-item[data-menu-index="${CSS.escape(index)}"] [aria-haspopup]`
    );
    next?.setAttribute('aria-expanded', 'true');
  }

  #clearActiveOverflow() {
    if (!this.#activeOverflowIndex) return;
    const link = this.querySelector(
      `.menu-list__list-item[data-menu-index="${CSS.escape(this.#activeOverflowIndex)}"] [aria-haspopup]`
    );
    link?.setAttribute('aria-expanded', 'false');
    this.#activeOverflowIndex = null;
  }

  /**
   * Begin (or extend) the close countdown. The active panel stays
   * paint-visible during the close transition; CSS handles the fade.
   */
  #scheduleClose() {
    if (!this.#activeIndex) return;
    this.#clearCloseTimer();
    this.#closeTimer = setTimeout(() => {
      this.#close();
    }, CLOSE_DELAY_MS);
  }

  /**
   * Close the active panel.
   * @param {{ immediate?: boolean }} [options]
   */
  #close({ immediate = false } = {}) {
    this.#clearCloseTimer();
    this.#clearClosingResetTimer();

    if (!this.#activeIndex) return;

    const row = this.#headerRow;
    const closingIndex = this.#activeIndex;

    this.#setLinkExpanded(closingIndex, false);
    if (closingIndex === 'more') this.#clearActiveOverflow();
    this.ariaExpanded = 'false';

    if (!row) {
      const panel = this.#getPanel(closingIndex);
      panel?.removeAttribute('data-active');
      this.#activeIndex = null;
      return;
    }

    if (immediate) {
      const panel = this.#getPanel(closingIndex);
      panel?.removeAttribute('data-active');
      row.dataset.menuState = 'closed';
      delete row.dataset.activeIndex;
      this.#activeIndex = null;
      return;
    }

    // Soft close: enter the closing state so CSS plays the transition.
    row.dataset.menuState = 'closing';
    this.#closingResetTimer = setTimeout(() => {
      // Only finalize if no one re-opened during the transition.
      if (row.dataset.menuState !== 'closing') return;
      const panel = this.#getPanel(closingIndex);
      panel?.removeAttribute('data-active');
      row.dataset.menuState = 'closed';
      delete row.dataset.activeIndex;
      this.#activeIndex = null;
    }, CLOSE_TRANSITION_MS);
  }

  #clearCloseTimer() {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  #clearClosingResetTimer() {
    if (this.#closingResetTimer) {
      clearTimeout(this.#closingResetTimer);
      this.#closingResetTimer = null;
    }
  }

  /**
   * Set aria-expanded on the link/button for a given index. For "more"
   * we set it on the More button only — individual overflow item
   * aria-expanded is managed by #setActiveOverflow.
   * @param {string} index
   * @param {boolean} expanded
   */
  #setLinkExpanded(index, expanded) {
    if (index === 'more') {
      const moreItem = /** @type {HTMLElement | null} */ (
        this.querySelector('.menu-list__list-item[slot="more"] [aria-haspopup]')
      );
      moreItem?.setAttribute('aria-expanded', String(expanded));
      return;
    }

    const link = /** @type {HTMLElement | null} */ (
      this.querySelector(`.menu-list__list-item[data-menu-index="${CSS.escape(index)}"] [aria-haspopup]`)
    );
    link?.setAttribute('aria-expanded', String(expanded));
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}
